import { useTranslation } from "react-i18next";
import { useState } from "react";
import { Label, TextField } from "react-aria-components";
import { Loader2 } from "lucide-react";
import type { Manual } from "@arc/contracts";
import type { AuthSession } from "../../auth/keycloak.js";
import { Dialog, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { RichTextEditor } from "@/components/ui/rich-text-editor";
import { useSetManualSettings, type ManualMutationError } from "./useManualMutations.js";
import { PlatformSelector, type ManualPlatforms } from "./PlatformSelector.js";

const PLATFORM_KEYS = ["is2dWebManual", "is3dWebManual", "isVrManual", "isArManual"] as const;

/** Mirrors the contract's `max(20000)` and `CreateManualDrawer`'s gate — the same limit, stated where the author is. */
const DESC_MAX = 20000;

export interface ManualSettingsDialogProps {
  manual: Manual;
  session: AuthSession | null;
  /** The caller holds a super-admin role (`/me.isSuper`). Gates the sensor-usage switch — the legacy's effective
   *  gate, since `sensorsEnabled` is false in every realm (ADR-040 D2). The server re-checks. */
  isSuper: boolean;
  onClose: () => void;
  /** The saved manual, so the view can refresh the drawer's snapshot in place. */
  onSaved: (m: Manual) => void;
}

/**
 * The manual's own settings (ADR-040) — the legacy manual-settings window's editable half: name, description,
 * where it is usable (ADR-057) and, for a SuperAdmin, sensor usage.
 *
 * The CATEGORY is deliberately not here: `moveManual` already is the legacy's category dropdown, and the drawer
 * offers it as Move. Created/edited, share links and the hand-speed rig live on the drawer itself.
 *
 * Opened from the editor's rail, where the legacy has these settings too.
 */
export function ManualSettingsDialog({ manual, session, isSuper, onClose, onSaved }: ManualSettingsDialogProps) {
  const { t } = useTranslation();
  const save = useSetManualSettings(session);
  const [name, setName] = useState(manual.name);
  const [description, setDescription] = useState(manual.description ?? "");
  const [platforms, setPlatforms] = useState<ManualPlatforms>({
    is2dWebManual: manual.is2dWebManual,
    is3dWebManual: manual.is3dWebManual,
    isVrManual: manual.isVrManual,
    isArManual: manual.isArManual,
  });
  const [isSensorManual, setIsSensorManual] = useState(manual.isSensorManual);

  const trimmed = name.trim();
  const descTooLong = description.length > DESC_MAX;
  const dirty =
    trimmed !== manual.name ||
    description !== (manual.description ?? "") ||
    PLATFORM_KEYS.some((k) => platforms[k] !== manual[k]) ||
    isSensorManual !== manual.isSensorManual;
  const error = save.error as (ManualMutationError & { body?: { sensors?: Array<{ slug: string }> } }) | undefined;

  const commit = () => {
    if (!trimmed || descTooLong) return; // belt-and-braces: Save is already disabled without a name, so this is unreachable from
    // the UI and no test can pin it. It exists so a future caller of `commit` cannot post a name the server's
    // `min(1)` would reject anyway.
    // PARTIAL: only what actually changed is sent. That is what lets a non-SuperAdmin rename — the server gates the
    // sensor flag on a real transition, and a body that carries an unchanged flag would be noise either way.
    save.mutate(
      {
        id: manual.id,
        ...(trimmed !== manual.name ? { name: trimmed } : {}),
        ...(description !== (manual.description ?? "") ? { description } : {}),
        ...Object.fromEntries(PLATFORM_KEYS.filter((k) => platforms[k] !== manual[k]).map((k) => [k, platforms[k]])),
        ...(isSensorManual !== manual.isSensorManual ? { isSensorManual } : {}),
      },
      { onSuccess: (m) => { onSaved(m); onClose(); } },
    );
  };

  return (
    <Dialog
      isOpen
      isDismissable={!save.isPending}
      isKeyboardDismissDisabled={save.isPending}
      onOpenChange={(open) => !open && onClose()}
    >
      <DialogTitle>{t("manualManagement.settings.title")}</DialogTitle>
      <DialogDescription>{t("manualManagement.settings.subtitle")}</DialogDescription>

      <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto py-1">
        <TextField value={name} onChange={setName} isDisabled={save.isPending} className="flex flex-col gap-1.5">
          <Label className="text-sm font-medium">{t("common.name")}</Label>
          <Input maxLength={255} autoFocus />
        </TextField>

        <div className="flex flex-col gap-1.5">
          <span id="ms-desc-label" className="text-sm font-medium">
            {t("manualManagement.misc.description")}
          </span>
          <RichTextEditor value={description} onChange={setDescription} ariaLabel={t("manualManagement.misc.description")} isDisabled={save.isPending} />
          {/* Rich-text markup inflates fast, and without this the whole PATCH — including an otherwise-valid rename —
              is rejected at the Zod boundary as a bare 400 with no hint which field was at fault (diff panel). */}
          {descTooLong ? (
            <p className="text-destructive text-xs">
              The description is too long ({description.length.toLocaleString()} / {DESC_MAX.toLocaleString()} characters).
            </p>
          ) : null}
        </div>

        <div className="flex flex-col gap-1.5">
          <span className="text-sm font-medium">{t("manualManagement.platforms.title")}</span>
          <PlatformSelector value={platforms} onChange={setPlatforms} isDisabled={save.isPending} />
        </div>

        {/* SuperAdmin-only (D2). Hidden rather than disabled: a switch nobody on this account can ever move is
            noise, and the legacy hides it the same way. */}
        {isSuper ? (
          <div className="flex flex-col gap-1">
            <Switch isSelected={isSensorManual} isDisabled={save.isPending} onChange={setIsSensorManual}>
              {t("manualManagement.misc.usesSensors")}
            </Switch>
            <p className="text-muted-foreground text-xs">
              {t("manualManagement.misc.turningThisOffIsOnlyPossible")}
              elements. It also switches the hand-speed check off.
            </p>
          </div>
        ) : null}
      </div>

      {error ? (
        <p className="text-destructive text-sm">
          {error.code === "SENSORS_STILL_BOUND"
            ? // Name WHICH devices — a generic "still in use" leaves the author hunting through every step.
              t("manualManagement.settings.sensorsAttached", {
                sensors: (error.body?.sensors ?? []).map((s) => s.slug).join(", ") || t("manualManagement.settings.sensorsFallback"),
              })
            : error.code === "SENSORS_FORBIDDEN"
              ? t("manualManagement.misc.onlyASuperAdminCanChange")
              : error.code === "MANUAL_LOCK_LOST"
                ? t("manualManagement.misc.someoneElseIsEditingThisManual")
                : error.code === "MANUAL_BODY_STALE"
                  ? t("manualManagement.misc.thisManualChangedWhileYouWere")
                  : error.status === 400
                    ? t("manualManagement.misc.theNameOrDescriptionIsToo") // the Zod boundary rejects before the handler, with no code
                    : t("manualManagement.misc.couldntSaveTheSettings")}
        </p>
      ) : null}

      <DialogFooter>
        <Button variant="ghost" onPress={onClose} isDisabled={save.isPending}>
          {t("manualManagement.misc.cancel")}
        </Button>
        <Button onPress={commit} isDisabled={!dirty || !trimmed || descTooLong || save.isPending}>
          {save.isPending ? <Loader2 aria-hidden="true" className="size-4 animate-spin" /> : null}
          {t("manualManagement.misc.save")}
        </Button>
      </DialogFooter>
    </Dialog>
  );
}
